import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const TermsAndConditions = () => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();

  useEffect(() => {
    const badge = document.getElementById('emergent-badge');
    if (badge) badge.style.display = 'none';
    return () => {
      if (badge) badge.style.display = 'flex';
    };
  }, []);

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-white'}`}>
      <div className="pt-[env(safe-area-inset-top,12px)]" />
      
      <header className={`sticky top-0 border-b z-50 ${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate('/settings')}
            className={`p-2 rounded-full transition-colors ${darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}
          >
            <ArrowLeft className={`w-6 h-6 ${darkMode ? 'text-white' : 'text-gray-700'}`} />
          </button>
          <div>
            <h1 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Terms & Conditions</h1>
          </div>
        </div>
      </header>

      <div className="px-6 py-6">
        <div className={`prose max-w-none ${darkMode ? 'prose-invert' : ''}`}>
          <h2 className={`text-2xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>SmartReadGo – Terms & Conditions</h2>
          <p className={`mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}><strong>Effective Date:</strong> 03 January 2026</p>
          
          <p className={`mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            These Terms & Conditions ("Terms") govern your access to and use of the SmartReadGo mobile application and related services (collectively, the "Service"). The Service is operated by SmartReadGo (Proprietorship), based in Mumbai, Maharashtra, India. By downloading, accessing, or using the Service, you agree to these Terms.
          </p>

          <h3 className={`text-xl font-bold mt-6 mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>1) What SmartReadGo is</h3>
          <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            SmartReadGo is a news/content discovery and listening app that helps users access content from third-party publishers and sources. The publisher/source name is displayed with the content. SmartReadGo does not publish original news reporting and does not claim ownership of third-party news content.
          </p>

          <h3 className={`text-xl font-bold mt-6 mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>2) Modes & features</h3>
          <ul className={`list-disc pl-6 mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            <li className="mb-2"><strong>Home Mode:</strong> View headlines and full articles, listen to summary or full article audio</li>
            <li className="mb-2"><strong>Drive Mode:</strong> Audio-first experience with limited visual interaction for safety</li>
          </ul>
          <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            <strong>Driving safety:</strong> If you use Drive Mode, you agree to comply with traffic laws and use the Service safely. You are solely responsible for safe driving.
          </p>

          <h3 className={`text-xl font-bold mt-6 mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>3) Eligibility</h3>
          <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            You must be at least 13 years old to use the Service. If you are under 18, you confirm you have permission from a parent/guardian.
          </p>

          <h3 className={`text-xl font-bold mt-6 mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>5) Content sources, ownership, and licenses</h3>
          <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            All third-party articles, images, trademarks, logos, and publisher content are owned by their respective rights holders. SmartReadGo does not grant you any license to third-party content beyond normal consumption within the Service.
          </p>

          <h3 className={`text-xl font-bold mt-6 mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>6) AI summaries and Text-to-Speech</h3>
          <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            SmartReadGo may generate AI summaries and audio narration using text-to-speech. These are provided for convenience and may contain errors. For full accuracy, refer to the original publisher article.
          </p>

          <h3 className={`text-xl font-bold mt-6 mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>7) Acceptable use</h3>
          <p className={`mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>You agree that you will not:</p>
          <ul className={`list-disc pl-6 mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            <li className="mb-2">Copy, scrape, or extract data from the Service using automated means</li>
            <li className="mb-2">Reproduce or commercially exploit third-party content without permission</li>
            <li className="mb-2">Bypass security or access controls</li>
            <li className="mb-2">Use the Service for unlawful purposes</li>
          </ul>

          <h3 className={`text-xl font-bold mt-6 mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>10) Complaints and takedown requests</h3>
          <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            If you are a publisher/rights holder or believe content infringes rights, contact us at <a href="mailto:Admin@smartreadgo.com" className="text-blue-600 hover:underline">Admin@smartreadgo.com</a> with article details, basis of complaint, and supporting proof.
          </p>

          <h3 className={`text-xl font-bold mt-6 mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>11) Disclaimers</h3>
          <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            The Service is provided "as is" and "as available". SmartReadGo does not guarantee uninterrupted or error-free operation. We are not responsible for third-party content accuracy or AI summary/TTS inaccuracies.
          </p>

          <h3 className={`text-xl font-bold mt-6 mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>16) Contact</h3>
          <p className={`mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>SmartReadGo (Proprietorship)</p>
          <p className={`mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Mumbai, Maharashtra, India</p>
          <p className={`mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Email: <a href="mailto:Admin@smartreadgo.com" className="text-blue-600 hover:underline">Admin@smartreadgo.com</a></p>
          <p className={`mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>WhatsApp: <a href="https://wa.me/919004685736" className="text-blue-600 hover:underline">+91 9004685736</a></p>

          <div className="h-20" />
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;
